/**
 * Online Order Home Page
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MapPin, Clock, ChevronRight, Star, ShoppingBag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useOrder } from '../context/OrderContext';
import { useCart } from '../context/CartContext';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

// opening_hours is free-form JSON per restaurant: { monday: "11-10" } or
// { monday: { open, close } } (+ an optional "timezone"). Unknown shapes show nothing.
function formatOpeningHours(hours) {
  if (!hours || typeof hours !== 'object') return [];
  return Object.entries(hours)
    .filter(([day]) => day !== 'timezone')
    .map(([day, value]) => {
      const label = day.charAt(0).toUpperCase() + day.slice(1);
      if (typeof value === 'string' && value.trim()) return `${label}: ${value}`;
      if (value && typeof value === 'object') {
        if (value.closed || value.is_closed) return `${label}: Closed`;
        if (value.open && value.close) return `${label}: ${value.open} - ${value.close}`;
      }
      return null;
    })
    .filter(Boolean);
}

export default function HomePage() {
  const [restaurant, setRestaurant] = useState(null);
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(true);
  const { orderType, setOrderType } = useOrder();
  const { restaurantSlug, getCartCount } = useCart();
  const navigate = useNavigate();


  // The ordering site serves one restaurant at a time; the cart remembers which
  const fetchRestaurantInfo = useCallback(async () => {
    if (!restaurantSlug) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/api/storefront/menu/${restaurantSlug}`);
      setRestaurant(data.restaurant);
      const items = (data.categories || []).flatMap((cat) => cat.items || []);
      const popular = items.filter((item) => item.is_popular);
      setFeatured(popular.length > 0 ? popular : items);
    } catch (error) {
      console.error('Failed to fetch info:', error);
    } finally {
      setLoading(false);
    }
  }, [restaurantSlug]);

  useEffect(() => {
    fetchRestaurantInfo();
  }, [fetchRestaurantInfo]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const cartCount = getCartCount();
  const hoursLines = formatOpeningHours(restaurant?.opening_hours);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center justify-between">
          <h1 className="text-xl font-bold">
            {restaurant?.name || 'Restaurant'}
          </h1>
          {cartCount > 0 && (
            <Link to="cart">
              <Button size="sm" className="bg-orange-600 hover:bg-orange-700">
                <ShoppingBag className="w-4 h-4 mr-2" />
                Cart ({cartCount})
              </Button>
            </Link>
          )}
        </div>
      </header>

      <main className="max-w-lg mx-auto">
        {/* Hero Banner */}
        <div className="relative h-48 bg-gradient-to-br from-orange-500 to-red-600">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center text-white">
              <h2 className="text-3xl font-bold mb-2">
                {restaurant?.name || 'Delicious Food'}
              </h2>
              {restaurant?.rating > 0 && (
                <div className="flex items-center justify-center gap-4 text-sm">
                  <span className="flex items-center gap-1">
                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    {Number(restaurant.rating).toFixed(1)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Order Type Selection */}
        <div className="p-4 bg-white border-b">
          <div className="flex gap-3">
            <Button
              variant={orderType === 'pickup' ? 'default' : 'outline'}
              className={`flex-1 ${orderType === 'pickup' ? 'bg-orange-600' : ''}`}
              onClick={() => setOrderType('pickup')}
            >
              <MapPin className="w-4 h-4 mr-2" />
              Pickup
            </Button>
            <Button
              variant={orderType === 'delivery' ? 'default' : 'outline'}
              className={`flex-1 ${orderType === 'delivery' ? 'bg-orange-600' : ''}`}
              onClick={() => setOrderType('delivery')}
            >
              <Clock className="w-4 h-4 mr-2" />
              Delivery
            </Button>
          </div>
          
          {orderType === 'pickup' && restaurant?.address && (
            <p className="text-sm text-slate-500 mt-3 flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              {restaurant.address}
            </p>
          )}
        </div>

        {/* Featured Items */}
        {featured.length > 0 && (
          <div className="p-4">
            <h3 className="font-bold text-lg mb-4">Popular Items</h3>
            <div className="space-y-3">
              {featured.slice(0, 4).map((item) => (
                <Link
                  key={item.id}
                  to="menu"
                  className="flex gap-4 p-3 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow"
                >
                  {item.image_url && (
                    <div className="w-20 h-20 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0">
                      <img 
                        src={item.image_url} 
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold">{item.name}</h4>
                    <p className="text-sm text-slate-500 line-clamp-2">{item.description}</p>
                    <p className="text-orange-600 font-bold mt-1">{formatCurrency(item.price)}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400 self-center" />
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Start Order Button */}
        <div className="p-4">
          <Button
            onClick={() => navigate('menu')}
            className="w-full h-14 text-lg bg-orange-600 hover:bg-orange-700"
          >
            View Full Menu
          </Button>
        </div>

        {/* Info */}
        <div className="p-4 space-y-4">
          {/* Hours: the restaurant's own opening_hours, shown only when set */}
          {hoursLines.length > 0 && (
            <div className="bg-white rounded-xl p-4">
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <Clock className="w-4 h-4 text-orange-500" />
                Hours
              </h4>
              {hoursLines.map((line) => (
                <p key={line} className="text-sm text-slate-600">{line}</p>
              ))}
            </div>
          )}

          {/* Location */}
          {restaurant?.address && (
            <div className="bg-white rounded-xl p-4">
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-orange-500" />
                Location
              </h4>
              <p className="text-sm text-slate-600">{restaurant.address}</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
